import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Plug, Plus, Trash2, Settings, Play, CheckCircle, XCircle,
  Loader2, ExternalLink, ChevronRight, Webhook, FileText,
  RefreshCw, Search, ArrowRight, Zap, Globe
} from 'lucide-react';

const CRM_LOGOS = {
  zoho: 'https://img.icons8.com/?size=48&id=VLKafOkk3sBX&format=png',
  hubspot: 'https://img.icons8.com/?size=48&id=rnJVCEWJZgHa&format=png',
  salesforce: 'https://img.icons8.com/?size=48&id=g4ib5O3QYCUQ&format=png',
};

const STATUS_COLORS = {
  success: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export default function IntegrationsPage() {
  const [tab, setTab] = useState('marketplace');
  const [templates, setTemplates] = useState([]);
  const [connections, setConnections] = useState([]);
  const [logs, setLogs] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [testing, setTesting] = useState(null);
  const [searchQ, setSearchQ] = useState('');

  const [form, setForm] = useState({
    name: '', webhook_url: '', api_key: '', auth_header: '', events: [], template_id: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, eRes, lRes] = await Promise.all([
        api.get('/integrations/templates'),
        api.get('/integrations/connections'),
        api.get('/integrations/event-types'),
        api.get('/integrations/logs?limit=30'),
      ]);
      setTemplates(tRes.data.templates || []);
      setConnections(cRes.data.connections || []);
      setEventTypes(eRes.data.event_types || []);
      setLogs(lRes.data.logs || []);
    } catch { toast.error('Failed to load integrations'); }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openSetup = (template) => {
    setForm({
      name: template.name, webhook_url: '', api_key: '', auth_header: '',
      events: template.supported_events || [], template_id: template.id,
    });
    setShowSetup(template);
  };

  const openCustom = () => {
    setForm({ name: 'Custom Webhook', webhook_url: '', api_key: '', auth_header: '', events: ['meeting.created'], template_id: '' });
    setShowCustom(true);
  };

  const createConnection = async () => {
    if (!form.webhook_url) { toast.error('Webhook URL is required'); return; }
    try {
      await api.post('/integrations/connections', form);
      toast.success(`${form.name} connected!`);
      setShowSetup(null);
      setShowCustom(false);
      fetchAll();
    } catch { toast.error('Failed to create connection'); }
  };

  const deleteConnection = async (id) => {
    try {
      await api.delete(`/integrations/connections/${id}`);
      toast.success('Connection removed');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleConnection = async (conn) => {
    try {
      await api.put(`/integrations/connections/${conn.id}`, { is_active: !conn.is_active });
      toast.success(conn.is_active ? 'Connection paused' : 'Connection activated');
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const testConnection = async (connId) => {
    setTesting(connId);
    try {
      const res = await api.post(`/integrations/connections/${connId}/test`);
      if (res.data.success) toast.success('Webhook test successful!');
      else toast.error(`Test failed: ${res.data.response?.slice(0, 100) || 'No response'}`);
      fetchAll();
    } catch { toast.error('Test failed'); }
    setTesting(null);
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQ.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQ.toLowerCase())
  );

  const connectedIds = new Set(connections.map(c => c.template_id));

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="integrations-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Integrations</h1>
            <p className="text-sm text-gray-500">Connect AIVORA Nexus with your favorite CRM and tools</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openCustom} data-testid="custom-webhook-btn">
              <Webhook className="w-4 h-4 mr-1" /> Custom Webhook
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Active Connections', value: connections.filter(c => c.is_active).length, icon: Plug, color: 'text-green-600 bg-green-50' },
            { label: 'Available CRMs', value: templates.length, icon: Globe, color: 'text-blue-600 bg-blue-50' },
            { label: 'Events Today', value: logs.filter(l => l.timestamp?.startsWith(new Date().toISOString().split('T')[0])).length, icon: Zap, color: 'text-purple-600 bg-purple-50' },
            { label: 'Success Rate', value: logs.length > 0 ? `${Math.round((logs.filter(l => l.status === 'success').length / logs.length) * 100)}%` : '---', icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50' },
          ].map((s, i) => (
            <Card key={s.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2.5 rounded-lg ${s.color}`}><s.icon className="w-5 h-5" /></div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
          {[
            { id: 'marketplace', label: 'CRM Marketplace', icon: Globe },
            { id: 'connections', label: 'My Connections', icon: Plug },
            { id: 'logs', label: 'Event Logs', icon: FileText },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            {/* Marketplace Tab */}
            {tab === 'marketplace' && (
              <div className="space-y-4">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search CRMs..." className="pl-9" value={searchQ} onChange={e => setSearchQ(e.target.value)} data-testid="crm-search" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTemplates.map(tmpl => {
                    const isConnected = connectedIds.has(tmpl.id);
                    return (
                      <Card key={tmpl.id} className={`transition-all hover:shadow-md ${isConnected ? 'ring-2 ring-green-200' : ''}`} data-testid={`crm-card-${tmpl.id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                              <img src={CRM_LOGOS[tmpl.id] || ''} alt={tmpl.name} className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                              <Plug className="w-6 h-6 text-gray-400" style={{ display: CRM_LOGOS[tmpl.id] ? 'none' : 'block' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900">{tmpl.name}</h3>
                                {isConnected && <Badge className="bg-green-100 text-green-700 text-[10px]">Connected</Badge>}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{tmpl.description}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {[...new Set((tmpl.supported_events || []).map(ev => ev.split('.')[0]))].slice(0, 5).map(entity => (
                              <Badge key={entity} variant="outline" className="text-[10px] capitalize">{entity}</Badge>
                            ))}
                            {(tmpl.supported_events || []).length > 5 && <Badge variant="outline" className="text-[10px]">+{tmpl.supported_events.length - 5}</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="flex-1 text-xs" onClick={() => openSetup(tmpl)} data-testid={`connect-${tmpl.id}`}>
                              {isConnected ? <Settings className="w-3 h-3 mr-1" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                              {isConnected ? 'Configure' : 'Connect'}
                            </Button>
                            {tmpl.docs_url && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(tmpl.docs_url, '_blank')}>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {/* Custom Webhook Card */}
                  <Card className="border-dashed border-2 border-gray-300 hover:border-blue-400 transition-all cursor-pointer" onClick={openCustom} data-testid="custom-webhook-card">
                    <CardContent className="p-5 flex flex-col items-center justify-center min-h-[200px] text-center">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
                        <Webhook className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Custom Webhook</h3>
                      <p className="text-xs text-gray-500 mt-1">Connect any CRM or tool via custom webhook URL</p>
                      <Button variant="outline" size="sm" className="mt-3 text-xs"><Plus className="w-3 h-3 mr-1" />Add Custom</Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Connections Tab */}
            {tab === 'connections' && (
              <div className="space-y-3">
                {connections.length === 0 ? (
                  <Card><CardContent className="p-10 text-center">
                    <Plug className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No connections yet</p>
                    <p className="text-xs text-gray-400 mt-1">Go to CRM Marketplace to connect your first CRM</p>
                    <Button size="sm" className="mt-4" onClick={() => setTab('marketplace')}><ArrowRight className="w-3 h-3 mr-1" />Browse CRMs</Button>
                  </CardContent></Card>
                ) : connections.map(conn => (
                  <Card key={conn.id} className={`transition-all ${!conn.is_active ? 'opacity-60' : ''}`} data-testid={`connection-${conn.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Plug className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 text-sm">{conn.name}</h3>
                            <Badge className={conn.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'} variant="outline">
                              {conn.is_active ? 'Active' : 'Paused'}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-400 truncate">{conn.webhook_url}</p>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                            <span>{conn.events?.length || 0} events</span>
                            <span className="text-green-600">{conn.success_count || 0} delivered</span>
                            {conn.fail_count > 0 && <span className="text-red-600">{conn.fail_count} failed</span>}
                            {conn.last_triggered && <span>Last: {new Date(conn.last_triggered).toLocaleString()}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline" size="sm" className="h-7 text-xs"
                            onClick={() => testConnection(conn.id)}
                            disabled={testing === conn.id}
                            data-testid={`test-conn-${conn.id}`}
                          >
                            {testing === conn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                            Test
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleConnection(conn)}>
                            {conn.is_active ? <XCircle className="w-4 h-4 text-orange-500" /> : <CheckCircle className="w-4 h-4 text-green-500" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteConnection(conn.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Logs Tab */}
            {tab === 'logs' && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Webhook Delivery Logs</CardTitle>
                    <Button variant="ghost" size="sm" className="h-7" onClick={fetchAll}><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {logs.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No webhook events yet. Create a meeting to trigger events.</div>
                  ) : (
                    <div className="divide-y divide-gray-100 max-h-[500px] overflow-auto">
                      {logs.map(log => (
                        <div key={log.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-sm" data-testid={`log-${log.id}`}>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800 text-xs">{log.event}</span>
                              <Badge className={`text-[10px] ${STATUS_COLORS[log.status] || 'bg-gray-100'}`}>{log.status}</Badge>
                              {log.status_code > 0 && <span className="text-[10px] text-gray-400">{log.status_code}</span>}
                            </div>
                            <p className="text-[10px] text-gray-400 truncate">{log.connection_name} - {log.response?.slice(0, 80)}</p>
                          </div>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* CRM Setup Dialog */}
        <Dialog open={!!showSetup} onOpenChange={() => setShowSetup(null)}>
          <DialogContent className="max-w-lg">
            {showSetup && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Plug className="w-5 h-5 text-blue-600" />
                    Connect {showSetup.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {/* Setup steps */}
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-blue-800 mb-2">Setup Instructions:</p>
                    <ol className="space-y-1.5">
                      {(showSetup.setup_steps || []).map((step, i) => (
                        <li key={`step-${i}-${step.slice(0, 10)}`} className="flex items-start gap-2 text-xs text-blue-700">
                          <span className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">{i + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <Input placeholder="Connection name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                  <Input placeholder="Webhook URL *" value={form.webhook_url} onChange={e => setForm({...form, webhook_url: e.target.value})} data-testid="webhook-url-input" />
                  <Input placeholder="API Key (optional)" type="password" value={form.api_key} onChange={e => setForm({...form, api_key: e.target.value})} />

                  {/* Event selection */}
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-2">Events to sync:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {eventTypes.map(et => (
                        <button
                          key={et.id}
                          onClick={() => {
                            const events = form.events.includes(et.id)
                              ? form.events.filter(e => e !== et.id)
                              : [...form.events, et.id];
                            setForm({...form, events});
                          }}
                          className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-all ${
                            form.events.includes(et.id) ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-500 border-gray-200'
                          }`}
                        >
                          {et.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {showSetup.docs_url && (
                    <a href={showSetup.docs_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                      <ExternalLink className="w-3 h-3" /> View {showSetup.name} API Documentation
                    </a>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSetup(null)}>Cancel</Button>
                  <Button onClick={createConnection} data-testid="save-connection-btn">
                    <Plug className="w-4 h-4 mr-1" /> Connect
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Custom Webhook Dialog */}
        <Dialog open={showCustom} onOpenChange={setShowCustom}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Webhook className="w-5 h-5 text-blue-600" />Custom Webhook</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-gray-500">Connect any CRM or automation tool (Zapier, Make, n8n) via a custom webhook URL.</p>
              <Input placeholder="Connection name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              <Input placeholder="Webhook URL *" value={form.webhook_url} onChange={e => setForm({...form, webhook_url: e.target.value})} data-testid="custom-webhook-url" />
              <Input placeholder="API Key / Bearer Token (optional)" type="password" value={form.api_key} onChange={e => setForm({...form, api_key: e.target.value})} />
              <Input placeholder="Custom Header Value (X-API-Key)" value={form.auth_header} onChange={e => setForm({...form, auth_header: e.target.value})} />
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Events:</p>
                <div className="flex flex-wrap gap-1.5">
                  {eventTypes.map(et => (
                    <button
                      key={et.id}
                      onClick={() => {
                        const events = form.events.includes(et.id) ? form.events.filter(e => e !== et.id) : [...form.events, et.id];
                        setForm({...form, events});
                      }}
                      className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-all ${
                        form.events.includes(et.id) ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-500 border-gray-200'
                      }`}
                    >
                      {et.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustom(false)}>Cancel</Button>
              <Button onClick={createConnection} data-testid="save-custom-webhook-btn"><Webhook className="w-4 h-4 mr-1" />Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
